import { useState, useCallback, useEffect, useRef } from 'react';
import healthOpsChatWebSocketService from '../services/api/healthOpsChatWebSocketService';
import { useAuthStore } from '../stores/authStore';
import { useHealthOpsChatStore } from '../stores/healthOpsChatStore';
import logger from '../utils/secureLogger';


export const useHealthOpsChat = () => {
  const { currentUser, getUserDisplayName } = useAuthStore();
  
  // Usar el store persistente para mensajes y sesi├│n
  const {
    conversations,
    currentSessionId,
    createSession,
    setCurrentSession,
    addMessage,
    getCurrentConversation
  } = useHealthOpsChatStore();

  // Inicializar o recuperar sesi├│n
  const [sessionId, setSessionId] = useState(() => {
    // Si ya existe una sesi├│n actual, usarla
    if (currentSessionId && conversations[currentSessionId]) {
      logger.debug('­ƒôé Recuperando sesi├│n existente:', currentSessionId);
      return currentSessionId;
    }

    // Crear nueva sesi├│n
    const userIdentifier = currentUser?.email || 
                          currentUser?.username || 
                          getUserDisplayName() || 
                          'anonymous';
    
    const sanitizedUser = userIdentifier.replace(/[^a-zA-Z0-9@._-]/g, '_');
    const timestamp = Date.now();
    const uuid = crypto.randomUUID().split('-')[0];
    
    const generatedSessionId = `${sanitizedUser}_${timestamp}_${uuid}`;
    const newId = createSession(generatedSessionId);
    
    return newId || generatedSessionId;
  });

  // Sincronizar sessionId con el store
  useEffect(() => {
    if (sessionId && sessionId !== currentSessionId) {
      setCurrentSession(sessionId);
    }
  }, [sessionId, currentSessionId, setCurrentSession]);

  const generateNewSession = useCallback(() => {
    const userIdentifier = currentUser?.email || 
                          currentUser?.username || 
                          getUserDisplayName() || 
                          'anonymous';
    
    const sanitizedUser = userIdentifier.replace(/[^a-zA-Z0-9@._-]/g, '_');
    const timestamp = Date.now();
    const uuid = crypto.randomUUID().split('-')[0];
    
    const newSessionId = `${sanitizedUser}_${timestamp}_${uuid}`;
    logger.debug('­ƒåò Nueva sesi├│n generada:', newSessionId);
    createSession(newSessionId);
    setSessionId(newSessionId);
    return newSessionId;
  }, [currentUser, getUserDisplayName, createSession]);

  // Obtener mensajes del store en lugar de estado local
  const currentConversation = getCurrentConversation();
  const messages = currentConversation?.messages || [];
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  
  const isSubscribed = useRef(false);

  useEffect(() => {
    if (isSubscribed.current) {
      return;
    }

    const connectWebSocket = async () => {
      setConnectionStatus('connecting');
      try {
        await healthOpsChatWebSocketService.connect();
      } catch (error) {
        setConnectionStatus('error');
        setError('Failed to connect to HealthOps agent. Please refresh the page.');
      }
    };

    connectWebSocket();

    const handleConnect = () => {
      setConnectionStatus('connected');
      setError(null);
      // Resetear estados de carga al reconectar
      setIsLoading(false);
      setIsTyping(false);
    };

    const handleDisconnect = (data) => {
      const previousStatus = connectionStatus;
      setConnectionStatus('disconnected');
      
      logger.debug('­ƒöî Desconectado:', data);
      
      // Resetear estados de carga al desconectar
      setIsLoading(false);
      setIsTyping(false);
      
      // Si el WebSocket se cerr├│ de forma anormal y est├íbamos conectados,
      // generar una nueva sesi├│n para el pr├│ximo mensaje
      if (data.shouldResetSession) {
        logger.debug('­ƒöä Sesi├│n perdida, se generar├í nueva sesi├│n en el pr├│ximo mensaje');
        // No generamos la sesi├│n aqu├¡, se generar├í cuando el usuario env├¡e el siguiente mensaje
      }
      
      // Solo mostrar error si est├íbamos conectados previamente
      if (previousStatus === 'connected' && !data.wasClean) {
        setError('Connection lost. Your next message will start a new session.');
      }
    };

    const handleMessage = (data) => {
      
      setIsTyping(false);
      setIsLoading(false);
      
      const agentResponse = {
        id: `${Date.now()}_${crypto.randomUUID().split('-')[0]}`, 
        type: "agent",
        content: data.content, 
        timestamp: data.timestamp,
        sessionId: data.sessionId
      };
      
      // Guardar mensaje en el store persistente
      addMessage(sessionId, agentResponse);
      setError(null); 
    };

    const handleError = (errorData) => {
      logger.error('­ƒö┤ Error del WebSocket:', errorData);
      
      setIsTyping(false);
      setIsLoading(false);
      
      // Ignorar errores de conexi├│n durante el proceso inicial de conexi├│n
      if (errorData.type === 'connection' && connectionStatus === 'connecting') {
        logger.debug('ÔÜá´©Å Error de conexi├│n durante inicio (ignorado):', errorData.message);
        return;
      }
      
      const errorMessage = errorData.message || 'An error occurred while communicating with HealthOps';
      
      // No bloquear el input, solo mostrar el error temporalmente
      setError(errorMessage);
      
      // Limpiar el error despu├®s de 5 segundos para permitir reintentos
      setTimeout(() => {
        setError(null);
      }, 5000);
      
      // Mostrar mensaje de error al usuario
      const errorResponse = {
        id: `${Date.now()}_${crypto.randomUUID().split('-')[0]}`,
        type: "agent",
        content: `ÔØî **Error**: ${errorMessage}\n\nPlease try again or contact support if the issue persists.`,
        timestamp: new Date()
      };
      
      // Guardar mensaje de error en el store
      addMessage(sessionId, errorResponse);
      
      if (errorData.type === 'connection') {
        setConnectionStatus('error');
      }
    };

    const handleReconnecting = (data) => {
      setConnectionStatus('reconnecting');
      setError(`Reconnecting... (attempt ${data.attempt}/${data.maxAttempts})`);
    };

    const handleMaxReconnectAttempts = () => {
      setConnectionStatus('error');
      setError('Unable to reconnect to HealthOps. Please refresh the page.');
    };

    const handleMessageSent = () => {
      // Mensaje enviado exitosamente
    };

    healthOpsChatWebSocketService.on('connect', handleConnect);
    healthOpsChatWebSocketService.on('disconnect', handleDisconnect);
    healthOpsChatWebSocketService.on('message', handleMessage);
    healthOpsChatWebSocketService.on('error', handleError);
    healthOpsChatWebSocketService.on('reconnecting', handleReconnecting);
    healthOpsChatWebSocketService.on('maxReconnectAttemptsReached', handleMaxReconnectAttempts);
    healthOpsChatWebSocketService.on('messageSent', handleMessageSent);

    isSubscribed.current = true;

    return () => {
      
      healthOpsChatWebSocketService.off('connect', handleConnect);
      healthOpsChatWebSocketService.off('disconnect', handleDisconnect);
      healthOpsChatWebSocketService.off('message', handleMessage);
      healthOpsChatWebSocketService.off('error', handleError);
      healthOpsChatWebSocketService.off('reconnecting', handleReconnecting);
      healthOpsChatWebSocketService.off('maxReconnectAttemptsReached', handleMaxReconnectAttempts);
      healthOpsChatWebSocketService.off('messageSent', handleMessageSent);
      
      healthOpsChatWebSocketService.disconnect();
      isSubscribed.current = false;
    };
  }, [sessionId]); 
  const sendMessage = useCallback((messageContent) => {
    if (!messageContent || !messageContent.trim()) {
      return;
    }

    if (isLoading) {
      return;
    }

    // Limpiar error previo al intentar enviar nuevo mensaje
    setError(null);

    // Si estamos desconectados o en error, intentar reconectar
    if (connectionStatus === 'disconnected' || connectionStatus === 'error') {
      logger.debug('­ƒöä Reconectando antes de enviar mensaje...');
      setConnectionStatus('connecting');
      healthOpsChatWebSocketService.connect()
        .then(() => {
          logger.debug('Ô£à Reconectado exitosamente');
          setConnectionStatus('connected');
          // Reintentar env├¡o despu├®s de reconectar
          setTimeout(() => sendMessage(messageContent), 500);
        })
        .catch((error) => {
          logger.error('ÔØî Error al reconectar:', error);
          setConnectionStatus('error');
          setError('Could not reconnect to HealthOps. Please refresh the page.');
          
          // Permitir reintentar despu├®s de 3 segundos
          setTimeout(() => {
            setError(null);
            setConnectionStatus('disconnected');
          }, 3000);
        });
      return;
    }

    // Si estamos conectando, esperar un momento
    if (connectionStatus === 'connecting') {
      logger.debug('ÔÅ│ Esperando conexi├│n...');
      setTimeout(() => sendMessage(messageContent), 1000);
      return;
    }

    // Usar la sesi├│n actual
    const currentSessionId = sessionId;

     const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent.trim(),
      timestamp: new Date()
    };

    // Guardar mensaje del usuario en el store
    addMessage(currentSessionId, userMessage);
    setInputMessage("");
    setIsLoading(true);
    setIsTyping(true);

   try {
      healthOpsChatWebSocketService.sendMessage(messageContent.trim(), currentSessionId);
      
      // Timeout para detectar respuestas lentas
      setTimeout(() => {
        if (isTyping) {
          logger.warn('ÔÅ▒´©Å Timeout esperando respuesta del agente');
          setIsTyping(false);
          setIsLoading(false);
          setError('Request timeout. The agent is taking too long to respond.');
          
          const timeoutMessage = {
            id: Date.now() + Math.random(),
            type: "agent",
            content: "ÔÅ▒´©Å **Timeout**: The request is taking longer than expected. Please try again.",
            timestamp: new Date()
          };
          addMessage(currentSessionId, timeoutMessage);
          
          // Limpiar error despu├®s de 5 segundos
          setTimeout(() => setError(null), 5000);
        }
      }, 30000); 

      
    } catch (error) {
      logger.error('ÔØî Error al enviar mensaje:', error);
      setIsTyping(false);
      setIsLoading(false);
      setError('Failed to send message. Please try again.');
      
      // Limpiar error despu├®s de 5 segundos
      setTimeout(() => setError(null), 5000);
    }
  }, [isLoading, connectionStatus, sessionId, isTyping, addMessage, setInputMessage]);

  const handleInputChange = useCallback((e) => {
    setInputMessage(e.target.value);
  }, []);

  
  const handleSendMessage = useCallback(() => {
    sendMessage(inputMessage);
  }, [inputMessage, sendMessage]);

  
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  
  const resetChat = useCallback(() => {
    logger.debug('­ƒöä Reseteando chat...');
    
    // Generar nueva sesi├│n en lugar de resetear la actual
    generateNewSession();
    setInputMessage("");
    setError(null);
    setIsTyping(false);
    setIsLoading(false);
  }, [generateNewSession]);

 
  const getDebugInfo = useCallback(() => {
    return {
      sessionId,
      connectionStatus,
      messageCount: messages.length,
      isLoading,
      isTyping,
      hasError: !!error,
      wsDebug: healthOpsChatWebSocketService.getDebugInfo()
    };
  }, [sessionId, connectionStatus, messages.length, isLoading, isTyping, error]);

  return {
    messages,
    inputMessage,
    isLoading,
    isTyping,
    error,
    connectionStatus,
    sessionId,
    
    handleInputChange,
    handleSendMessage,
    sendMessage,
    clearError,
    resetChat,
    
    getDebugInfo
  };
};
